// Controlled Argentine peso input. It preserves what the operator typed while
// showing the exact amount that would be stored in cents.

import type { ChangeEvent, InputHTMLAttributes, ReactNode } from "react";
import { forwardRef, useId, useMemo, useState } from "react";
import {
  ARS_MAX_AMOUNT_CENTS,
  formatARSExact,
  getArsAmountPolicy,
  parseArsInput,
  type ArsAmountPolicy,
  type ParseArsInputResult,
} from "../lib/money";

export type MoneyInputChange = {
  rawValue: string;
  parsed: ParseArsInputResult;
  policy: ArsAmountPolicy | null;
};

type Props = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type" | "inputMode" | "value" | "defaultValue" | "onChange"
> & {
  /** Visible label for the field. */
  label?: ReactNode;
  /** Text as entered by the operator. This component never reformats it. */
  value: string;
  /** Update the raw text in the owning form. */
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  /** Server-side or form-level error, displayed alongside local validation. */
  error?: string;
  /** Optional help shown before the preview. */
  helpText?: ReactNode;
  /** Receives the parsed cents and policy on every operator edit. */
  onAmountChange?: (change: MoneyInputChange) => void;
};

const wrapStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--space-2)",
};

const labelStyle: React.CSSProperties = {
  fontSize: "var(--font-size-14)",
  color: "var(--text-secondary)",
  fontWeight: 500,
};

const inputStyle: React.CSSProperties = {
  minHeight: 48,
  padding: "0 var(--space-3)",
  fontSize: "var(--font-size-16)",
  borderRadius: "var(--radius-md)",
  border: "1px solid var(--border-default)",
  background: "var(--surface-base)",
  color: "var(--text-primary)",
  outline: "none",
  width: "100%",
};

const messageStyle: React.CSSProperties = {
  margin: 0,
  fontSize: "var(--font-size-14)",
  fontWeight: 500,
};

function appendDescribedBy(...ids: Array<string | undefined>) {
  return ids.filter(Boolean).join(" ") || undefined;
}

/**
 * Monetary text input for amounts that are about to be persisted.
 *
 * It intentionally uses a text field, rather than `type=number`, because
 * Argentine thousands and decimal separators must remain visible and exact.
 */
export const MoneyInput = forwardRef<HTMLInputElement, Props>(function MoneyInput(
  {
    label,
    value,
    onChange,
    onAmountChange,
    error,
    helpText,
    id,
    style,
    onBlur,
    "aria-describedby": ariaDescribedBy,
    "aria-invalid": ariaInvalid,
    ...rest
  },
  ref
) {
  const generatedId = useId();
  const inputId = id ?? `money-input-${generatedId}`;
  const previewId = `${inputId}-preview`;
  const feedbackId = `${inputId}-feedback`;
  const [touched, setTouched] = useState(false);

  const parsed = useMemo(() => parseArsInput(value), [value]);
  const policy = parsed.ok ? getArsAmountPolicy(parsed.cents) : null;
  const blocked = policy === "blocked";
  const localError = !parsed.ok && touched ? parsed.message : undefined;
  const displayedError = error ?? localError;
  const hasError = Boolean(displayedError) || blocked;

  const preview = parsed.ok ? `Se registrará: ${formatARSExact(parsed.cents)}` : null;
  const warning = policy === "confirmation_required"
    ? "El importe es inusualmente alto. Al guardar, requerirá una confirmación adicional."
    : null;
  const blockMessage = blocked
    ? `El importe supera el máximo permitido de ${formatARSExact(ARS_MAX_AMOUNT_CENTS)}.`
    : null;
  const feedback = displayedError ?? blockMessage;

  return (
    <div style={wrapStyle}>
      {label && <label style={labelStyle} htmlFor={inputId}>{label}</label>}
      <input
        ref={ref}
        id={inputId}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        value={value}
        aria-invalid={ariaInvalid ?? hasError}
        aria-describedby={appendDescribedBy(ariaDescribedBy, helpText ? `${inputId}-help` : undefined, preview ? previewId : undefined, feedback ? feedbackId : undefined)}
        style={{
          ...inputStyle,
          borderColor: hasError ? "var(--color-danger)" : warning ? "var(--color-warning)" : "var(--border-default)",
          ...style,
        }}
        onChange={(event) => {
          setTouched(true);
          onChange(event);
          const nextParsed = parseArsInput(event.target.value);
          onAmountChange?.({
            rawValue: event.target.value,
            parsed: nextParsed,
            policy: nextParsed.ok ? getArsAmountPolicy(nextParsed.cents) : null,
          });
        }}
        onBlur={(event) => {
          setTouched(true);
          onBlur?.(event);
        }}
        {...rest}
      />
      {helpText && <p id={`${inputId}-help`} style={{ ...messageStyle, color: "var(--text-secondary)" }}>{helpText}</p>}
      {preview && <p id={previewId} className="num" style={{ ...messageStyle, color: "var(--text-brand)" }}>{preview}</p>}
      {warning && <p style={{ ...messageStyle, color: "var(--color-warning)" }} role="status">{warning}</p>}
      {feedback && <p id={feedbackId} style={{ ...messageStyle, color: "var(--color-danger)" }} role="alert">{feedback}</p>}
    </div>
  );
});
